"use client";
import { twMerge } from "tailwind-merge";
import type { LinkComponentType } from "../../index";
import type { CellEmphasis } from "../DataTable/DataTable.types";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";
import { TextLink } from "../TextLink";

export interface SiteMetaDisplayProps {
  /** Street address (combined street lines) */
  streetAddress: string;

  /** City name (optional) */
  city?: string;

  /** State/province (optional) */
  state?: string;

  /** Postal/ZIP code (optional) */
  postalCode?: string;

  /** Layout variant - stacked (vertical) or inline (horizontal) */
  layout?: "stacked" | "inline";

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Link to site detail page (optional) */
  href?: string;

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Link variant for the address */
  linkVariant?: "default" | "brand" | "muted" | "unstyled";

  /** Text emphasis level - "strong"/"high" for bold, "normal" for regular, "low" for muted */
  emphasis?: CellEmphasis;

  /** Loading state */
  isLoading?: boolean;

  /** Additional classes */
  className?: string;

  /**
   * Entity identifier for PII masking (e.g., "site:site_abc123")
   * When provided, adds data-pii-* attributes for the Curtain extension
   */
  piiEntity?: string;
}

/**
 * SiteMetaDisplay
 *
 * Reusable component for displaying site location information.
 * Supports stacked and inline layouts with size variants and optional linking.
 *
 * **Stacked layout**: MapPin icon, street address above, city/state/zip below
 * **Inline layout**: MapPin icon + street address only (no city/state)
 *
 * Use this component directly in cards, lists, or other layouts.
 * For DataTable cells, use SiteMetaCell which wraps this component.
 *
 * @example
 * ```tsx
 * // Stacked layout (default)
 * <SiteMetaDisplay
 *   streetAddress="123 Main St"
 *   city="San Francisco"
 *   state="CA"
 *   postalCode="94102"
 *   layout="stacked"
 *   size="md"
 *   href="/sites/123"
 * />
 *
 * // Inline layout
 * <SiteMetaDisplay
 *   streetAddress="123 Main St"
 *   layout="inline"
 *   size="sm"
 *   href="/sites/123"
 * />
 * ```
 */
export function SiteMetaDisplay({
  streetAddress,
  city,
  state,
  postalCode,
  layout = "stacked",
  size = "md",
  href,
  LinkComponent,
  linkVariant = "unstyled",
  emphasis = "normal",
  isLoading = false,
  className,
  piiEntity,
}: SiteMetaDisplayProps) {
  // Size-based styling
  // iconContainerSize matches DeviceMetaDisplay's logoSize for vertical alignment
  // skeletonIconStacked matches container size, skeletonIconInline matches bare icon size
  const sizes = {
    xs: {
      iconSize: 16,
      iconContainerSize: "w-7 h-7",
      gap: "gap-1.5",
      textSize: "text-xs",
      secondaryTextSize: "text-xs",
      skeletonIconStacked: 28,
      skeletonIconInline: 16,
      skeletonText: 80,
      skeletonSecondary: 60,
    },
    sm: {
      iconSize: 20,
      iconContainerSize: "w-8 h-8",
      gap: "gap-2",
      textSize: "text-sm",
      secondaryTextSize: "text-xs",
      skeletonIconStacked: 32,
      skeletonIconInline: 20,
      skeletonText: 96,
      skeletonSecondary: 72,
    },
    md: {
      iconSize: 24,
      iconContainerSize: "w-9 h-9",
      gap: "gap-3",
      textSize: "text-base",
      secondaryTextSize: "text-sm",
      skeletonIconStacked: 36,
      skeletonIconInline: 24,
      skeletonText: 128,
      skeletonSecondary: 96,
    },
    lg: {
      iconSize: 28,
      iconContainerSize: "w-10 h-10",
      gap: "gap-3",
      textSize: "text-lg",
      secondaryTextSize: "text-base",
      skeletonIconStacked: 40,
      skeletonIconInline: 28,
      skeletonText: 160,
      skeletonSecondary: 120,
    },
  };

  const sizeConfig = sizes[size];

  // Map emphasis to font weight and color
  const emphasisClasses = {
    strong: "font-semibold text-text-body",
    high: "font-semibold text-text-body",
    normal: "text-text-body",
    low: "text-text-muted",
  };
  const emphasisClass = emphasisClasses[emphasis];

  // Build city/state/zip line
  const locationParts: string[] = [];
  if (city) locationParts.push(city);
  if (state && postalCode) {
    locationParts.push(`${state} ${postalCode}`);
  } else if (state) {
    locationParts.push(state);
  } else if (postalCode) {
    locationParts.push(postalCode);
  }
  const secondLine = locationParts.join(", ");

  // Render link wrapper
  const LinkWrapper = ({ children, className: linkClassName }: { children: React.ReactNode; className?: string }) => {
    if (!href) {
      return <span className={linkClassName}>{children}</span>;
    }

    if (LinkComponent) {
      return (
        <LinkComponent href={href} className={linkClassName}>
          {children}
        </LinkComponent>
      );
    }

    return (
      <TextLink
        href={href}
        variant={linkVariant}
        className={twMerge("hover:underline", linkClassName)}
        asButton={false}
      >
        {children}
      </TextLink>
    );
  };

  // Loading state
  if (isLoading) {
    if (layout === "inline") {
      return (
        <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
          <Skeleton
            width={sizeConfig.skeletonIconInline}
            height={sizeConfig.skeletonIconInline}
            className="flex-shrink-0 rounded"
          />
          <Skeleton width={sizeConfig.skeletonText} height={sizeConfig.skeletonIconInline * 0.8} />
        </div>
      );
    }

    return (
      <div className={twMerge("flex items-center", sizeConfig.gap, className)}>
        <Skeleton
          width={sizeConfig.skeletonIconStacked}
          height={sizeConfig.skeletonIconStacked}
          className="flex-shrink-0 rounded"
        />
        <div className="flex flex-col gap-1">
          <Skeleton width={sizeConfig.skeletonText} height={16} />
          <Skeleton width={sizeConfig.skeletonSecondary} height={12} />
        </div>
      </div>
    );
  }

  // Inline layout: Icon + street address only
  if (layout === "inline") {
    return (
      <LinkWrapper
        className={twMerge(
          "flex items-center",
          sizeConfig.gap,
          href && "hover:bg-background-hover transition-colors",
          className
        )}
      >
        <Icon name="MapPin" size={sizeConfig.iconSize} className="text-text-muted flex-shrink-0" />
        <span
          className={twMerge(
            "leading-normal truncate hover:text-text-body",
            emphasisClass,
            sizeConfig.textSize,
            href && "hover:underline hover:decoration-1 hover:underline-offset-4"
          )}
          data-pii-type={piiEntity ? "address" : undefined}
          data-pii-entity={piiEntity}
        >
          {streetAddress}
        </span>
      </LinkWrapper>
    );
  }

  // Stacked layout: Icon, street address above, city/state/zip below
  // Determine the appropriate PII type for the location line based on what's present
  const getLocationPiiType = () => {
    if (!piiEntity) return undefined;
    if (city && state && postalCode) return "city-state-zip";
    if (city && state) return "city-state";
    if (city) return "city";
    if (state) return "state";
    if (postalCode) return "postal-code";
    return undefined;
  };

  return (
    <div className={twMerge("flex items-center justify-start", sizeConfig.gap, className)}>
      <div
        className={twMerge(
          "flex-shrink-0 flex items-center justify-center rounded border border-border-muted",
          sizeConfig.iconContainerSize
        )}
      >
        <Icon name="MapPin" size={sizeConfig.iconSize} className="text-text-muted" />
      </div>
      <div className="flex flex-col items-start min-w-0">
        <LinkWrapper
          className={twMerge(
            "leading-normal truncate hover:text-text-body",
            emphasisClass,
            sizeConfig.textSize,
            href && "hover:underline hover:decoration-1 hover:underline-offset-4"
          )}
        >
          <span data-pii-type={piiEntity ? "address" : undefined} data-pii-entity={piiEntity}>
            {streetAddress}
          </span>
        </LinkWrapper>
        {secondLine && (
          <div
            className={twMerge("text-text-caption leading-normal truncate", sizeConfig.secondaryTextSize)}
            data-pii-type={getLocationPiiType()}
            data-pii-entity={piiEntity}
          >
            {secondLine}
          </div>
        )}
      </div>
    </div>
  );
}
